import { Menu, Radio, Table } from 'antd';
import { ColumnGroupType, ColumnType } from 'antd/lib/table';
import moment, { Moment } from 'moment';
import { PureComponent, ReactNode, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import getLedger, { LedgerObject } from '../api/getLedger';
import { AntTooltip } from '../components/common/AntTooltip';
import { RangeValue } from '../components/common/DatePicker';
import NavBar from '../components/common/NavBar';
import NewHeaderComponent from '../components/common/NewHeaderComponent';
import DateFilter from '../components/headerfilters/DateFilter';
import EmptyView from '../components/MyKhata/EmptyView';
import FetchError from '../components/MyKhata/FetchError';
import MyKhataStatusFilters from '../components/MyKhata/MyKhataStatusFilter';
import { RightPanel } from '../components/MyKhata/RightPanel';

import {
    GO_TO_ADD_EKA_CREDITS,
    GO_TO_MY_KHATA,
    PaymentsDashboardQueryParams,
} from '../routes/paths';
import { Actions } from '../store/actions';
import { setKhataCredits } from '../store/actions/session';
import { GlobalState } from '../store/reducers';
import { track } from '../tracking';
import { indexAllPatients } from './allPatientsIndex';
import { captureError } from './captureError';
import { Helmet } from 'react-helmet';
import { ekaCreditsIcon } from '../components/common/Icons';
import { GetType } from '../components/MyKhata/Utils';
import DownloadKhataInvoiceModal from '../components/MyKhata/DownloadInvoice';
import { getDoc, getItemFromMisc } from '../store/idbHandler';
import { getMActiveId } from '../store/localstorage';
import { GenieSettings } from '../api/pushGenie';
import { BizSettings } from '../api/fetchMyGenie';

export type FetchState = 'loading' | 'success' | 'error' | '';
export type KhataStatusFilterType =
    | 'all-transactions'
    | 'total-credited'
    | 'total-debited'
    | 'used-for-messages';
export type KhataStatusLabelType =
    | 'All transactions'
    | 'Total credited'
    | 'Total debited'
    | 'Used for messages';
export type TimeFilterType =
    | 'today'
    | 'yesterday'
    | 'last-7-days'
    | 'current-month'
    | 'last-3-months';

type State = {
    fetchState: FetchState;
    filteredTransactions: LedgerObject[];
    lastUpdated: string;
    currTimeFilter?: string;
    dates?: RangeValue<Moment> | undefined;
    khataCredits?: number;
    switchColumn: FetchState;
    onAppPtsCount?: number;
    showInvoiceModal?: boolean;
};

type RouterProps = {
    khataStatusFilter: KhataStatusFilterType;
};

interface Props extends RouteComponentProps<RouterProps> {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
}

export const timeFiltersObj: {
    [k in TimeFilterType]: {
        id: TimeFilterType;
        label: string;
        startDate: string;
        endDate: string;
    };
} = {
    today: {
        id: 'today',
        label: 'Today',
        startDate: moment().startOf('day').toISOString(),
        endDate: moment().endOf('day').toISOString(),
    },
    yesterday: {
        id: 'yesterday',
        label: 'Yesterday',
        startDate: moment().subtract(1, 'days').startOf('day').toISOString(),
        endDate: moment().subtract(1, 'days').endOf('day').toISOString(),
    },
    'last-7-days': {
        id: 'last-7-days',
        label: 'Last 7 Days',
        startDate: moment().subtract(6, 'days').startOf('day').toISOString(),
        endDate: moment().endOf('day').toISOString(),
    },
    'current-month': {
        id: 'current-month',
        label: 'Current Month',
        startDate: moment().startOf('month').toISOString(),
        endDate: moment().add(1, 'days').startOf('day').toISOString(),
    },
    'last-3-months': {
        id: 'last-3-months',
        label: 'Last 3 Months',
        startDate: moment().subtract(3, 'months').startOf('day').toISOString(),
        endDate: moment().add(1, 'days').startOf('day').toISOString(),
    },
};

type TransactionsWithMeta = {
    transactions: LedgerObject[];
    amount?: number;
};

export const khataFiltersObj: {
    [k in KhataStatusFilterType]: {
        id: KhataStatusFilterType;
        label: KhataStatusLabelType;
        getData: (array: LedgerObject[]) => TransactionsWithMeta;
    };
} = {
    'all-transactions': {
        id: 'all-transactions',
        label: 'All transactions',
        getData: (array: LedgerObject[]) => ({
            transactions: array,
        }),
    },
    'total-credited': {
        id: 'total-credited',
        label: 'Total credited',
        getData: (array: LedgerObject[]) => ({
            transactions: array.filter((i) => i.tx_type === 'CREDIT'),
        }),
    },
    'total-debited': {
        id: 'total-debited',
        label: 'Total debited',
        getData: (array: LedgerObject[]) => ({
            transactions: array.filter((i) => i.tx_type === 'DEBIT'),
        }),
    },
    'used-for-messages': {
        id: 'used-for-messages',
        label: 'Used for messages',
        getData: (array: LedgerObject[]) => ({
            transactions: array.filter((i) => i.type === 'wa' || i.type === 'sms'),
        }),
    },
};

export type FilterData = { [k in KhataStatusFilterType]: { count: number; amount?: number } };

export const KHATA_CREDITS = 'khataCredits';
class MyKhata extends PureComponent<Props, State> {
    counts: FilterData | undefined = undefined;
    allTransactions: LedgerObject[] = [];

    initialState: State = {
        fetchState: '',
        filteredTransactions: [],
        lastUpdated: new Date().toISOString(),
        switchColumn: 'success',
        showInvoiceModal: false,
    };

    state: State = {
        ...this.initialState,
        dates: undefined,
        currTimeFilter: timeFiltersObj['today'].label,
        onAppPtsCount: 0,
    };

    componentDidMount = (): void => {
        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();
        if (!startDate && !endDate) {
            this.goToDefaultUrl();
        }
        this.getAllTransactions();

        indexAllPatients()
            .then((data) => {
                this.setState({ onAppPtsCount: data.onAppPtsCount });
            })
            .catch(console.error);
    };

    componentDidUpdate = (prevProps: Props): void => {
        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();

        const previousStartDate =
            new URLSearchParams(prevProps.location.search).get(
                PaymentsDashboardQueryParams.start_date,
            ) || '';
        const previousEndDate =
            new URLSearchParams(prevProps.location.search).get(
                PaymentsDashboardQueryParams.end_date,
            ) || '';

        if (previousStartDate !== startDate || previousEndDate !== endDate) {
            this.getAllTransactions();
        }
    };

    goToDefaultUrl = (): void => {
        const { startDate, endDate } = timeFiltersObj['today'];
        const { khataStatusFilter } = this.props.match.params;
        return this.props.history.replace(
            GO_TO_MY_KHATA(khataStatusFilter || 'all-transactions', startDate, endDate),
        );
    };

    getStartDateAndEndDateFromUrl = (): { startDate: string; endDate: string } => {
        const startDate =
            new URLSearchParams(this.props.location.search).get(
                PaymentsDashboardQueryParams.start_date,
            ) || '';

        const endDate =
            new URLSearchParams(this.props.location.search).get(
                PaymentsDashboardQueryParams.end_date,
            ) || '';

        return { startDate, endDate };
    };

    getAllTransactions = (): void => {
        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();
        const { khataStatusFilter } = this.props.match.params;

        if (
            !moment(startDate).isValid() ||
            !moment(endDate).isValid() ||
            !Object.keys(khataFiltersObj).includes(khataStatusFilter)
        ) {
            this.goToDefaultUrl();
            return;
        }

        this.setState({
            ...this.initialState,
            fetchState: 'loading',
            lastUpdated: new Date().toISOString(),
        });

        this.fetchTransactions(startDate, endDate, khataStatusFilter);
    };

    fetchTransactions = (
        startDate: string,
        endDate: string,
        khataStatusFilter: KhataStatusFilterType,
    ): void => {
        const { getData: getTransactions } = khataFiltersObj[khataStatusFilter];
        const { dispatch } = this.props;

        getLedger(startDate, endDate)
            .then((res) => {
                if (res.success) {
                    const filterData: FilterData = {} as FilterData;
                    (Object.keys(khataFiltersObj) as KhataStatusFilterType[]).forEach((k) => {
                        const t = khataFiltersObj[k].getData(res.ledger);
                        const amount = t.transactions.reduce((acc, cur) => {
                            const { amount = 0 } = cur;
                            return acc + Math.abs(amount);
                        }, 0);
                        filterData[k] = {
                            count: t.transactions.length,
                            amount: +amount.toFixed(2),
                        };
                        this.counts = filterData;
                        this.allTransactions = res.ledger;
                        const balance = Math.round(res.balance);
                        this.setState({
                            fetchState: 'success',
                            filteredTransactions: getTransactions(res.ledger).transactions,
                            lastUpdated: new Date().toISOString(),
                            khataCredits: Math.round(balance),
                        });
                        dispatch(setKhataCredits(balance));
                        localStorage.setItem(KHATA_CREDITS, String(balance));
                    });
                } else {
                    this.counts = undefined;
                    this.setState({
                        fetchState: 'error',
                    });
                }
            })
            .catch((e) => {
                this.counts = undefined;
                this.setState({
                    fetchState: 'error',
                });
                track('ERROR', { on: 'getLedger', what: (e as Error).message });
                captureError(e);
            });
    };

    setTimeFilter = (startDate: string, endDate: string): void => {
        const {
            startDate: currentStartDate,
            endDate: currentEndDate,
        } = this.getStartDateAndEndDateFromUrl();
        if (moment(startDate).isSame(currentStartDate) && moment(endDate).isSame(currentEndDate)) {
            return;
        }
        const { khataStatusFilter } = this.props.match.params;
        this.props.history.replace(GO_TO_MY_KHATA(khataStatusFilter, startDate, endDate));
        this.fetchTransactions(startDate, endDate, khataStatusFilter);
        track('MY_KHATA_DATE_FILTER', {
            startDate: moment(startDate).format('lll'),
            endDate: moment(endDate).format('lll'),
            khataStatusFilter,
        });
    };

    setKhataFilter = (khataFilter: KhataStatusFilterType): void => {
        const { khataStatusFilter } = this.props.match.params;
        const { getData: getTransactions } = khataFiltersObj[khataFilter];
        if (khataStatusFilter === khataFilter) {
            return;
        }
        const { allTransactions } = this;

        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();

        const transactions = getTransactions(allTransactions).transactions;

        this.props.history.replace(GO_TO_MY_KHATA(khataFilter, startDate, endDate));
        this.setState({
            filteredTransactions: transactions,
            switchColumn: transactions.length ? 'loading' : 'success',
        });

        setTimeout(() => {
            this.setState({
                switchColumn: 'success',
            });
        }, 150);
    };

    renderComponent = (transactions: LedgerObject[]) => {
        const doc = getDoc();
        const columns: (ColumnGroupType<LedgerObject> | ColumnType<LedgerObject>)[] = [
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">Seq</span>
                ),
                width: 75,
                key: 'index',
                render: (_: LedgerObject, __: LedgerObject, index: number): ReactNode => {
                    return (
                        <p className="text-14 text-darwin-neutral-900 whitespace-nowrap">
                            {index + 1}
                        </p>
                    );
                },
                fixed: 'left',
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Narrative
                    </span>
                ),
                width: 200,
                key: 'narrative',
                dataIndex: 'narrative',
                render: (narrative: string): ReactNode => {
                    return <p>{narrative}</p>;
                },
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">type</span>
                ),
                width: 120,
                key: 'type',
                dataIndex: 'type',
                render: (type: string): ReactNode => <GetType type={type} />,
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Last Transaction Date
                    </span>
                ),
                width: 200,
                key: 'last_tx_date',
                dataIndex: 'last_tx_date',
                render: (last_tx_date: string): ReactNode => {
                    return (
                        <p className="text-14 text-darwin-neutral-900 whitespace-nowrap">
                            {moment(last_tx_date).format('MMMM Do YYYY, h:mm:ss a')}
                        </p>
                    );
                },
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Total TXNs
                    </span>
                ),
                width: 120,
                key: 'length',
                dataIndex: 'length',
                render: (length: number): ReactNode => {
                    return (
                        <p className="text-14 text-darwin-neutral-900 whitespace-nowrap">
                            {length}
                        </p>
                    );
                },
                sorter: ({ length: a = 0 }, { length: b = 0 }) => a - b,
            },
            {
                title: (
                    <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                        Amount
                    </span>
                ),
                width: 80,
                key: 'amount',
                dataIndex: 'amount',
                render: (amount: number): ReactNode => {
                    const cssClassName =
                        amount > 0
                            ? 'text-darwin-green-primary'
                            : amount < 0
                            ? 'text-red-primary'
                            : '';
                    return (
                        <p
                            className={`text-14 text-darwin-neutral-900 whitespace-nowrap ${cssClassName}`}
                        >
                            {amount > 0 ? '+' : '-'} {Math.abs(amount)}
                        </p>
                    );
                },
                sorter: ({ amount: a = 0 }, { amount: b = 0 }) => a - b,
            },
            {
                title: (
                    <span className="flex items-center justify-center">
                        <span className="font-700 text-12 text-darwin-neutral-700 uppercase">
                            Invoice
                        </span>
                    </span>
                ),
                key: 'download',
                dataIndex: 'download',
                render: (amount: number, record: LedgerObject): ReactNode => {
                    return record.type === 'purchased' && doc ? (
                        <AntTooltip content="Download invoice">
                            <span
                                className="cursor-pointer flex justify-center"
                                onClick={() => {
                                    track('SHOW_INVOICE_MODAL', {
                                        totalTxns: String(record.length),
                                    });

                                    this.toggleInvoiceModal(true);
                                }}
                            >
                                <svg
                                    className="w-14 h-14 fill-darwin-accent-bright-blue-800"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M7.60938 1.23438V8.11523L10.0215 5.70312C10.25 5.44922 10.6309 5.44922 10.8848 5.70312C11.1133 5.93164 11.1133 6.3125 10.8848 6.54102L7.40625 9.99414C7.17773 10.248 6.79688 10.248 6.56836 9.99414L3.11523 6.54102C2.86133 6.3125 2.86133 5.93164 3.11523 5.70312C3.34375 5.44922 3.72461 5.44922 3.97852 5.70312L6.39062 8.11523V1.23438C6.39062 0.904297 6.64453 0.625 7 0.625C7.33008 0.625 7.60938 0.904297 7.60938 1.23438ZM3.75 8.34375L4.96875 9.5625H2.125C1.89648 9.5625 1.71875 9.76562 1.71875 9.96875V12C1.71875 12.2285 1.89648 12.4062 2.125 12.4062H11.875C12.0781 12.4062 12.2812 12.2285 12.2812 12V9.96875C12.2812 9.76562 12.0781 9.5625 11.875 9.5625H9.00586L10.2246 8.34375H11.875C12.7637 8.34375 13.5 9.08008 13.5 9.96875V12C13.5 12.9141 12.7637 13.625 11.875 13.625H2.125C1.21094 13.625 0.5 12.9141 0.5 12V9.96875C0.5 9.08008 1.21094 8.34375 2.125 8.34375H3.75ZM11.4688 10.9844C11.4688 11.3398 11.1895 11.5938 10.8594 11.5938C10.5039 11.5938 10.25 11.3398 10.25 10.9844C10.25 10.6543 10.5039 10.375 10.8594 10.375C11.1895 10.375 11.4688 10.6543 11.4688 10.9844Z" />
                                </svg>
                            </span>
                        </AntTooltip>
                    ) : (
                        <span className="flex justify-center">
                            <span>-</span>
                        </span>
                    );
                },
                width: 60,
            },
        ];
        return this.state.fetchState === 'loading' ? (
            <div className="h-full flex items-center justify-center">
                <svg
                    className="w-24 h-24 fill-actionPrimary animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                </svg>
            </div>
        ) : this.state.fetchState !== 'error' && transactions.length ? (
            <Table
                columns={columns}
                className="antd-table-dashboard"
                dataSource={transactions}
                pagination={false}
                sticky={true}
                scroll={{
                    x: 900,
                }}
            />
        ) : this.state.fetchState === 'error' ? (
            <FetchError onRefresh={this.getAllTransactions} />
        ) : (
            <EmptyView />
        );
    };

    handleClick = (key: TimeFilterType) => {
        const { startDate, endDate } = timeFiltersObj[key];
        this.setTimeFilter(startDate, endDate);
        this.setState({
            currTimeFilter: timeFiltersObj[key].label,
            dates: undefined,
        });
    };

    daysFilter = () => (
        <Menu className="w-184 rounded-8 pt-0">
            <div className="font-lato border-b border-darwin-neutral-100 flex gap-8 py-8 px-16 text-13 items-center font-700">
                <svg
                    width="14"
                    height="13"
                    viewBox="0 0 14 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M0.5 2.00391C0.5 1.41992 0.957031 0.9375 1.54102 0.9375H12.4336C13.0176 0.9375 13.5 1.41992 13.5 2.00391C13.5 2.25781 13.3984 2.48633 13.2461 2.66406L9.03125 7.86914V11.5C9.03125 11.957 8.65039 12.3125 8.19336 12.3125C8.01562 12.3125 7.83789 12.2617 7.68555 12.1348L5.34961 10.2812C5.0957 10.0781 4.96875 9.79883 4.96875 9.49414V7.86914L0.728516 2.66406C0.576172 2.48633 0.5 2.25781 0.5 2.00391ZM1.87109 2.15625L6.03516 7.25977C6.13672 7.38672 6.1875 7.51367 6.1875 7.64062V9.39258L7.8125 10.6875V7.64062C7.8125 7.51367 7.83789 7.38672 7.93945 7.25977L12.1035 2.15625H1.87109Z"
                        fill="#484848"
                    />
                </svg>
                Filter
            </div>
            {Object.entries(timeFiltersObj).map(([k, v]) => (
                <Menu.Item className="py-8 px-16">
                    <Radio
                        defaultChecked={v.label === timeFiltersObj['today'].label}
                        value={v.label}
                        onClick={() => this.handleClick(k as TimeFilterType)}
                        disabled={this.state.fetchState === 'loading'}
                        checked={v.label === this.state.currTimeFilter}
                        className={`${
                            v.label === this.state.currTimeFilter
                                ? 'filter-radio'
                                : 'add-new-pt-radio'
                        }`}
                    >
                        <p
                            className={`select-none font-lato text-neutral-black text-400 text-13 ${
                                v.label === this.state.currTimeFilter && 'font-700'
                            }`}
                        >
                            {v.label}
                        </p>
                    </Radio>
                </Menu.Item>
            ))}
        </Menu>
    );

    exportAsExcel = (): void => {
        const xlsx = document.createElement('script');
        xlsx.onload = this.createExcelAndDownload;
        xlsx.src = '/xlsx-sheet.js';
        document.getElementsByTagName('head')[0].appendChild(xlsx);
    };

    toggleInvoiceModal = (val: boolean): void => {
        this.setState({ showInvoiceModal: val });
    };

    createExcelAndDownload = (): void => {
        track('EXPORT_EXCEL_MY_KHATA');
        const { allTransactions } = this;

        const requiredData = allTransactions.map((i, index) => {
            return {
                'Sr No': index + 1,
                Description: i.narrative,
                'Transaction Type': i.tx_type,
                'Transaction Sub Type': i.type,
                'Total Amount': i.amount,
                'Total Transactions': i.length,
                'Last Transaction Date': i.last_tx_date,
            };
        });

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const ws = XLSX.utils.json_to_sheet(requiredData);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const wb = XLSX.utils.book_new();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        XLSX.utils.book_append_sheet(wb, ws, 'Eka Billing');

        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();

        const start = moment(startDate).format('DD-MM-YYYY');
        const end = moment(endDate).format('DD-MM-YYYY');

        const filename = `My-Eka-Credits-${start}-to-${end}.xls`;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        XLSX.writeFile(wb, filename);
    };

    onAddEkaCredits = () => {
        const { history } = this.props;
        history.push(GO_TO_ADD_EKA_CREDITS);
        track('ADD_EKA_CREDITS');
    };

    render(): ReactNode {
        const {
            filteredTransactions,
            lastUpdated,
            fetchState,
            khataCredits,
            switchColumn,
            onAppPtsCount,
            showInvoiceModal,
        } = this.state;
        const { khataStatusFilter } = this.props.match.params;
        const { counts } = this;

        const transactions: LedgerObject[] = filteredTransactions;

        const purchasedTxns = transactions?.filter((tr) => tr.type === 'purchased')[0]
            ?.transactions;

        return (
            <div className="grid h-full overflow-x-auto overflow-y-hidden grid-cols-queueGrid min-w-840">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>EKA - My Eka Credits</title>
                </Helmet>
                <NavBar />
                <div className="flex flex-col h-full col-span-11 overflow-hidden">
                    <NewHeaderComponent
                        title="My Eka Credits"
                        filterComponent={
                            <div className="flex w-full justify-between items-stretch gap-8">
                                <div className="flex relative top-2 items-center gap-4">
                                    <p className="text-neutral-5 text-13">
                                        Last updated at {moment(lastUpdated).format('hh:mm A')}
                                    </p>
                                    <AntTooltip content="Refresh">
                                        <div className="flex justify-between p-4 select-none items-center space-x-16">
                                            <div className="flex space-x-8 items-center">
                                                <button
                                                    onClick={this.getAllTransactions}
                                                    className="flex space-x-8 items-center border-b border-transparent cursor-pointer text-actionPrimary hover:text-actionPrimary-hover hover:border-actionPrimary-hover font-700 focus:outline-none text-14"
                                                >
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        className="fill-darwin-accent-bright-blue-800"
                                                        viewBox="0 0 512 512"
                                                    >
                                                        <path d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88C178.39 88.068 111.52 141.178 93.057 214.85C91.713 220.213 86.935 224 81.406 224H24.103C16.605 224 10.909 217.193 12.296 209.824C33.933 94.924 134.813 8 256 8C322.448 8 382.791 34.136 427.315 76.685L463.03 40.97C478.149 25.851 504 36.559 504 57.941V192C504 205.255 493.255 216 480 216H345.941C324.559 216 313.851 190.149 328.97 175.029L370.72 133.28V133.28ZM32 296H166.059C187.441 296 198.149 321.851 183.03 336.971L141.28 378.721C172.542 407.994 213.115 424.04 256.156 424.001C333.574 423.931 400.471 370.857 418.943 297.152C420.287 291.789 425.065 288.002 430.594 288.002H487.898C495.396 288.002 501.092 294.809 499.705 302.178C478.067 417.076 377.187 504 256 504C189.552 504 129.209 477.864 84.685 435.315L48.97 471.03C33.851 486.149 8 475.441 8 454.059V320C8 306.745 18.745 296 32 296Z"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </AntTooltip>
                                </div>
                                <div className="flex gap-8 h-36 items-stretch">
                                    <DateFilter
                                        currTimeFilter={this.state.currTimeFilter}
                                        daysFilter={this.daysFilter}
                                        setTimeFilter={(startDate: string, endDate: string) => {
                                            this.setState({
                                                currTimeFilter: 'Custom',
                                            });
                                            this.setTimeFilter(startDate, endDate);
                                        }}
                                        datePickerDisabled={this.state.fetchState === 'loading'}
                                        noOfDays={90}
                                        dates={this.state.dates}
                                        setDates={(val) => this.setState({ dates: val })}
                                    />
                                    <button
                                        className="flex items-center px-12 py-10 text-white bg-darwin-accent-bright-blue-800 rounded-8 font-700"
                                        onClick={this.onAddEkaCredits}
                                    >
                                        Add Eka Credits
                                    </button>
                                </div>
                            </div>
                        }
                    />

                    <div className="flex-1 flex p-16 space-x-16">
                        <div className="flex-1 flex flex-col">
                            {/* <PurchaseEkaCreditsStrip /> */}
                            <div className="flex items-center space-x-8">
                                <div>{ekaCreditsIcon()}</div>
                                <p className="text-28 font-700">You have</p>
                                {typeof khataCredits === 'number' && (
                                    <p
                                        className={`px-12 text-white ${
                                            khataCredits <= 20
                                                ? 'bg-darwin-status-warning-500'
                                                : 'bg-darwin-green-primary'
                                        } rounded-8 text-20 font-700`}
                                    >
                                        {khataCredits}
                                    </p>
                                )}
                                <p className="text-28 font-700">Eka Credits</p>

                                <div className="flex items-center gap-8 pl-16">
                                    <button className="px-8 py-6 flex items-center space-x-8 rounded-8 border border-darwin-accent-bright-blue-800">
                                        <span
                                            className="text-darwin-accent-bright-blue-800 text-13 font-700"
                                            onClick={this.exportAsExcel}
                                        >
                                            Download report
                                        </span>
                                        <span>
                                            <svg
                                                className="w-14 h-14 fill-darwin-accent-bright-blue-800"
                                                viewBox="0 0 14 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M7.60938 1.23438V8.11523L10.0215 5.70312C10.25 5.44922 10.6309 5.44922 10.8848 5.70312C11.1133 5.93164 11.1133 6.3125 10.8848 6.54102L7.40625 9.99414C7.17773 10.248 6.79688 10.248 6.56836 9.99414L3.11523 6.54102C2.86133 6.3125 2.86133 5.93164 3.11523 5.70312C3.34375 5.44922 3.72461 5.44922 3.97852 5.70312L6.39062 8.11523V1.23438C6.39062 0.904297 6.64453 0.625 7 0.625C7.33008 0.625 7.60938 0.904297 7.60938 1.23438ZM3.75 8.34375L4.96875 9.5625H2.125C1.89648 9.5625 1.71875 9.76562 1.71875 9.96875V12C1.71875 12.2285 1.89648 12.4062 2.125 12.4062H11.875C12.0781 12.4062 12.2812 12.2285 12.2812 12V9.96875C12.2812 9.76562 12.0781 9.5625 11.875 9.5625H9.00586L10.2246 8.34375H11.875C12.7637 8.34375 13.5 9.08008 13.5 9.96875V12C13.5 12.9141 12.7637 13.625 11.875 13.625H2.125C1.21094 13.625 0.5 12.9141 0.5 12V9.96875C0.5 9.08008 1.21094 8.34375 2.125 8.34375H3.75ZM11.4688 10.9844C11.4688 11.3398 11.1895 11.5938 10.8594 11.5938C10.5039 11.5938 10.25 11.3398 10.25 10.9844C10.25 10.6543 10.5039 10.375 10.8594 10.375C11.1895 10.375 11.4688 10.6543 11.4688 10.9844Z" />
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center space-x-8 px-10 py-6 bg-gradient-to-r from-status-infoBG text-status-info select-none">
                                <p className="border border-status-info rounded-8 px-8 py-1 font-700">
                                    Pro Tip
                                </p>
                                <div className="flex items-center space-x-5">
                                    <p>{onAppPtsCount} of your patients are on</p>
                                    <p>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            className="w-16 h-16 m-auto cursor-pointer"
                                        >
                                            <path
                                                d="M22,7a4.34,4.34,0,0,1-1.26,2.81L13.3,17.27a5.46,5.46,0,0,1-.64.53,4.33,4.33,0,0,1-6.72-4h0A1.45,1.45,0,1,1,8.36,15,2,2,0,0,0,11,16.1h0l.19-.08a2.59,2.59,0,0,0,.5-.37L15.33,12,11.67,8.29a2,2,0,0,0-2.83,0,1.91,1.91,0,0,0-.53.91,1.45,1.45,0,0,1-1.75,2.32,1.51,1.51,0,0,1-.57-1A4.32,4.32,0,0,1,13.3,6.69L17,10.33,19.1,8.22a2,2,0,0,0,.07-2.78,9.7,9.7,0,1,0-.67,13.7h0c.23-.21.45-.43.66-.65a2,2,0,0,0-.07-2.78l-1.55-1.55,1.63-1.63,1.55,1.55a4.31,4.31,0,0,1,.15,6,12,12,0,1,1-.8-17c.27.26.54.53.8.81A4.27,4.27,0,0,1,22,7Z"
                                                fill="#7265E3"
                                            />
                                        </svg>
                                    </p>
                                    <p>
                                        Eka Care App. They will get{' '}
                                        <span className="font-700">
                                            Notifications Delivered to app for FREE.
                                        </span>
                                    </p>
                                </div>
                            </div>

                            <MyKhataStatusFilters
                                data={counts}
                                isLoading={fetchState === 'loading'}
                                setFilter={this.setKhataFilter}
                                khataStatusFilter={khataStatusFilter}
                            />

                            <div className="relative flex-1 w-full table-fader border border-neutral-7">
                                {switchColumn === 'loading' && (
                                    <div className="flex items-center justify-center absolute inset-0 z-10 bg-white">
                                        <svg
                                            className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                        </svg>
                                    </div>
                                )}
                                <div className="absolute inset-0 overflow-y-scroll">
                                    {this.renderComponent(transactions)}
                                </div>
                            </div>
                        </div>
                        <RightPanel />
                        <DownloadKhataInvoiceModal
                            isVisible={!!showInvoiceModal}
                            closeMe={() => this.toggleInvoiceModal(false)}
                            purchasedTxns={purchasedTxns}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default connect()(withRouter(MyKhata));

export const PurchaseEkaCreditsStrip = () => {
    const [canShowPurchaseEkaCreditsPopover, setCanShowPurchaseEkaCreditsPopover] = useState(false);
    const history = useHistory();

    useEffect(() => {
        getShowPurchaseEkaCreditsPopover();
    }, []);

    const getShowPurchaseEkaCreditsPopover = async () => {
        const myId = getMActiveId();
        if (!myId) {
            return true;
        }
        const { showPurchaseEkaCreditsPopover = true } = (await getItemFromMisc<BizSettings>(
            myId,
            'bizSettings',
        )) || { showPurchaseEkaCreditsPopover: true };
        setCanShowPurchaseEkaCreditsPopover(showPurchaseEkaCreditsPopover);
    };

    const onAddEkaCredits = () => {
        history.push(GO_TO_ADD_EKA_CREDITS);
        track('ADD_EKA_CREDITS');
    };

    if (!canShowPurchaseEkaCreditsPopover) {
        return null;
    }

    return (
        <div
            className="flex items-center justify-between px-16 py-8 rounded-8 mb-8"
            style={{
                background: 'linear-gradient(93deg, #19A66A 0%, #75A6CA 97.81%)',
            }}
        >
            <p className="text-14 text-white">
                <span className="font-700">🎁 Doctors’ Day Offer:</span> Recharge & get bonus Eka
                Credits up to 20% — Only this week!
            </p>
            <button
                className="px-10 py-6 text-12 text-darwin-accent-bright-blue-800 font-700 rounded-4 bg-white"
                onClick={onAddEkaCredits}
            >
                Recharge now
            </button>
        </div>
    );
};
